import React from "react";
import { ArrowRight, Phone, Activity, Cctv, ScanLine, Radar, MapPin, Signal } from "lucide-react";

const head = { fontFamily: "'Barlow Condensed', sans-serif" };
const mono = { fontFamily: "'Roboto Mono', monospace" };
const body = { fontFamily: "'Inter', sans-serif" };

const metrics = [
  { k: "ACTIVE FEEDS", v: "248" },
  { k: "PLATES / DAY", v: "1.2K" },
  { k: "RESPONSE", v: "8s" },
  { k: "UPTIME", v: "99.9%" },
];

const modules = [
  { icon: Cctv, title: "Camera Grid", tag: "LIVE", desc: "Unified multi-site feed wall monitored by your dedicated guard in real time." },
  { icon: ScanLine, title: "LPR Engine", tag: "AUTO", desc: "Plate capture, watchlist matching and instant unauthorized-access alerts." },
  { icon: Radar, title: "Threat Detection", tag: "AI", desc: "Behavioural anomaly flags surfaced before an incident occurs." },
  { icon: MapPin, title: "Access Control", tag: "GATE", desc: "Remote verification of every visitor, vendor and delivery at the perimeter." },
];

const Option3Tactical = () => {
  return (
    <div style={body} className="bg-[#E2E8F0] text-[#0A0A0A]">
      {/* Nav */}
      <header className="bg-white/70 backdrop-blur border-b border-[#0A0A0A]/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF3B30] flex items-center justify-center"><Signal className="w-4 h-4 text-white" /></div>
            <span style={head} className="text-2xl font-extrabold tracking-wide uppercase">Redline</span>
            <span style={mono} className="text-[10px] text-[#007AFF] ml-1 mt-1">v2.0</span>
          </div>
          <nav style={mono} className="hidden lg:flex items-center gap-8 text-xs uppercase tracking-wider text-[#0A0A0A]/60">
            <a href="#" className="hover:text-[#FF3B30]">// guards</a>
            <a href="#" className="hover:text-[#FF3B30]">// tech</a>
            <a href="#" className="hover:text-[#FF3B30]">// vs_others</a>
            <a href="#" className="hover:text-[#FF3B30]">// deploy</a>
          </nav>
          <button style={head} className="bg-[#0A0A0A] text-white px-6 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#FF3B30] transition-colors flex items-center gap-2"><Phone className="w-4 h-4" /> Consult</button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div style={mono} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#00FF94] px-3 py-1.5 mb-8 text-xs uppercase tracking-wider">
              <span className="w-2 h-2 bg-[#00FF94] rounded-full animate-pulse" /> command_center::online
            </div>
            <h1 style={head} className="text-7xl xl:text-8xl font-extrabold uppercase leading-[0.85] tracking-tight mb-7">
              Tactical<br />virtual<br /><span className="text-[#FF3B30]">security.</span>
            </h1>
            <p className="text-lg text-[#0A0A0A]/70 max-w-lg mb-9 leading-relaxed">
              A dedicated guard backed by a full command-center stack — LPR, AI anomaly detection and a unified camera grid. The most advanced HOA protection your community can deploy.
            </p>
            <div className="flex flex-wrap gap-4">
              <button style={head} className="bg-[#FF3B30] text-white px-8 py-4 text-base font-bold uppercase tracking-wide flex items-center gap-2 hover:bg-[#0A0A0A] transition-colors">Deploy a Guard <ArrowRight className="w-4 h-4" /></button>
              <button style={head} className="bg-white border border-[#0A0A0A]/15 px-8 py-4 text-base font-bold uppercase tracking-wide hover:border-[#007AFF] hover:text-[#007AFF] transition-colors">View the Stack</button>
            </div>
          </div>
          {/* Dashboard mock */}
          <div className="bg-[#0A0A0A] rounded-xl p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <span style={mono} className="text-[#00FF94] text-xs flex items-center gap-2"><Activity className="w-4 h-4" /> LIVE MONITORING</span>
              <span style={mono} className="text-white/40 text-xs">CAM 24 / 248</span>
            </div>
            <div className="relative rounded-lg overflow-hidden mb-4">
              <img src="https://images.pexels.com/photos/14990053/pexels-photo-14990053.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Security camera" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#FF3B30]/40" />
              <div className="absolute top-3 left-3 border border-[#FF3B30] w-24 h-16" />
              <div style={mono} className="absolute bottom-3 left-3 bg-[#FF3B30] text-white text-[10px] px-2 py-0.5">PLATE: TX·8KG·204</div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {metrics.map((m) => (
                <div key={m.k} className="bg-white/5 border border-white/10 rounded p-3">
                  <div style={mono} className="text-[#007AFF] text-lg font-bold">{m.v}</div>
                  <div style={mono} className="text-white/40 text-[9px] uppercase tracking-wide mt-1">{m.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section className="max-w-7xl mx-auto px-8 py-16">
        <div className="flex items-end justify-between mb-10 border-b border-[#0A0A0A]/15 pb-5">
          <h2 style={head} className="text-5xl font-extrabold uppercase tracking-tight">The Stack</h2>
          <span style={mono} className="text-xs text-[#0A0A0A]/50 uppercase">04 integrated modules</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {modules.map((m) => (
            <div key={m.title} className="bg-white border border-[#0A0A0A]/10 rounded-lg p-6 hover:border-[#FF3B30] hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-center justify-between mb-5">
                <m.icon className="w-7 h-7 text-[#0A0A0A]" />
                <span style={mono} className="text-[10px] bg-[#007AFF]/10 text-[#007AFF] px-2 py-0.5 rounded">{m.tag}</span>
              </div>
              <h3 style={head} className="text-2xl font-bold uppercase tracking-tight mb-2">{m.title}</h3>
              <p className="text-sm text-[#0A0A0A]/60 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-8 pb-24">
        <div className="bg-[#0A0A0A] rounded-2xl px-10 py-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-20" style={{ backgroundImage: "linear-gradient(#FF3B30 1px,transparent 1px),linear-gradient(90deg,#FF3B30 1px,transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative">
            <span style={mono} className="text-[#00FF94] text-xs uppercase tracking-wider">// ready_to_deploy</span>
            <h2 style={head} className="text-5xl xl:text-6xl font-extrabold uppercase text-white tracking-tight my-5 max-w-2xl">Outgun the competition's outdated patrols.</h2>
            <button style={head} className="bg-[#FF3B30] text-white px-10 py-5 text-lg font-bold uppercase tracking-wide flex items-center gap-3 hover:bg-white hover:text-[#0A0A0A] transition-colors">Request a Consultation <ArrowRight className="w-5 h-5" /></button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Option3Tactical;
